import { useState, useEffect } from 'react'
import { type MRT_PaginationState, type MRT_Row, type MRT_SortingState } from 'material-react-table'
import {
  getBrands,
  getBrandsCount,
  createBrand,
  updateBrand,
  deleteBrand,
  getNotes,
  getNotesCount,
  createNote,
  updateNote,
  deleteNote,
  getPerfumers,
  getPerfumersCount,
  createPerfumer,
  updatePerfumer,
  deletePerfumer,
  getCountries,
  getCountriesCount,
  createCountry,
  updateCountry,
  deleteCountry,
  type IBrand,
  type INote,
  type IPerfumer,
  type ICountry,
} from '../../../pocketbase/PocketbaseMetadataService'
import { MetadataTable } from './MetadataTable'
import { ConfirmationDialog } from '../../../components/dialogs/ConfirmationDialog'
import { ImageUploadDialog } from '../../../components/dialogs/ImageUploadDialog'
import { updateMetadataImage } from '../../../pocketbase/PocketbaseMetadataService'
import { pb } from '../../../pocketbase/Pocketbase'

type MetadataType = 'brands' | 'notes' | 'perfumers' | 'countries'

type MetadataItem = IBrand | INote | IPerfumer | ICountry

export const MetadataView = () => {
  const [metadataType, setMetadataType] = useState<MetadataType>('brands')
  const [items, setItems] = useState<MetadataItem[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [pagination, setPagination] = useState<MRT_PaginationState>({
    pageIndex: 0,
    pageSize: 10,
  })
  const [sorting, setSorting] = useState<MRT_SortingState>([])
  const [rowCount, setRowCount] = useState(0)
  const [searchValue, setSearchValue] = useState('')

  // Dialog states
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [imageUploadDialogOpen, setImageUploadDialogOpen] = useState(false)
  const [selectedRow, setSelectedRow] = useState<MRT_Row<MetadataItem> | null>(null)

  // Reset pagination and search when metadata type changes
  useEffect(() => {
    setPagination({ pageIndex: 0, pageSize: 10 })
    setSorting([])
    setSearchValue('')
  }, [metadataType])

  // Load total count when metadata type changes
  useEffect(() => {
    loadTotalCount()
  }, [metadataType])

  // Reset to first page when search changes (but not on initial mount)
  useEffect(() => {
    setPagination(prev => ({ ...prev, pageIndex: 0 }))
  }, [searchValue])

  // Load items when pagination, metadata type, or search changes
  useEffect(() => {
    loadItems()
  }, [pagination.pageIndex, pagination.pageSize, metadataType, searchValue, sorting])

  const loadTotalCount = async () => {
    try {
      let count: number
      switch (metadataType) {
        case 'brands':
          count = await getBrandsCount()
          break
        case 'notes':
          count = await getNotesCount()
          break
        case 'perfumers':
          count = await getPerfumersCount()
          break
        case 'countries':
          count = await getCountriesCount()
          break
      }
      setRowCount(count)
    } catch (error) {
      console.error(`Error loading ${metadataType} count:`, error)
    }
  }

  const loadItems = async () => {
    try {
      setIsLoading(true)

      // Pocketbase uses 1-indexed pages, Material React Table uses 0-indexed
      const page = pagination.pageIndex + 1
      const search = searchValue ? searchValue.trim() : undefined

      // Determine sort string
      let sort = 'name'
      const imageSort = sorting.find(s => s.id === 'image')
      if (imageSort) {
        // -image sorts descending (literals first, empty last usually, or standard string compare)
        // Generally for strings: "http..." > ""
        // So DESC (-image) puts images at top
        // ASC (image) puts images at bottom
        sort = imageSort.desc ? '-image' : 'image'
      }

      let result
      switch (metadataType) {
        case 'brands':
          result = await getBrands(page, pagination.pageSize, search, sort)
          break
        case 'notes':
          result = await getNotes(page, pagination.pageSize, search, sort)
          break
        case 'perfumers':
          result = await getPerfumers(page, pagination.pageSize, search)
          break
        case 'countries':
          result = await getCountries(page, pagination.pageSize, search)
          break
      }

      setItems(result.items)
      setRowCount(result.totalItems)
    } catch (error) {
      console.error(`Error loading ${metadataType}:`, error)
    } finally {
      setIsLoading(false)
    }
  }

  // CREATE
  const handleCreate = async (values: MetadataItem) => {
    try {
      switch (metadataType) {
        case 'brands':
          await createBrand(values.name)
          break
        case 'notes':
          await createNote(values.name)
          break
        case 'perfumers':
          await createPerfumer(values.name)
          break
        case 'countries':
          await createCountry(values.name)
          break
      }

      // Reload the current page to show the new item
      await loadItems()
      await loadTotalCount()
    } catch (error) {
      console.error(`Error creating ${metadataType.slice(0, -1)}:`, error)
      alert(`Failed to create ${metadataType.slice(0, -1)}. Please try again.`)
    }
  }

  // UPDATE
  const handleUpdate = async (values: MetadataItem) => {
    try {
      switch (metadataType) {
        case 'brands':
          await updateBrand(values.id, values.name)
          break
        case 'notes':
          await updateNote(values.id, values.name)
          break
        case 'perfumers':
          await updatePerfumer(values.id, values.name)
          break
        case 'countries':
          await updateCountry(values.id, values.name)
          break
      }

      // Update local state
      setItems(
        items.map((item) =>
          item.id === values.id ? { ...item, name: values.name } : item,
        ),
      )
    } catch (error) {
      console.error(`Error updating ${metadataType.slice(0, -1)}:`, error)
      alert(`Failed to update ${metadataType.slice(0, -1)}. Please try again.`)
    }
  }

  // DELETE
  const handleDelete = (row: MRT_Row<MetadataItem>) => {
    setSelectedRow(row)
    setDeleteDialogOpen(true)
  }

  const confirmDelete = async () => {
    if (!selectedRow) return

    try {
      switch (metadataType) {
        case 'brands':
          await deleteBrand(selectedRow.original.id)
          break
        case 'notes':
          await deleteNote(selectedRow.original.id)
          break
        case 'perfumers':
          await deletePerfumer(selectedRow.original.id)
          break
        case 'countries':
          await deleteCountry(selectedRow.original.id)
          break
      }

      setDeleteDialogOpen(false)
      setSelectedRow(null)

      // Reload the current page
      await loadItems()
      await loadTotalCount()
    } catch (error) {
      console.error(`Error deleting ${metadataType.slice(0, -1)}:`, error)
      alert(`Failed to delete ${metadataType.slice(0, -1)}. Please try again.`)
    }
  }

  const getEntityName = () => {
    switch (metadataType) {
      case 'brands':
        return 'Brand'
      case 'notes':
        return 'Note'
      case 'perfumers':
        return 'Perfumer'
      case 'countries':
        return 'Country'
    }
  }

  const getColumnHeader = () => {
    switch (metadataType) {
      case 'brands':
        return 'Brand Name'
      case 'notes':
        return 'Note Name'
      case 'perfumers':
        return 'Perfumer Name'
      case 'countries':
        return 'Country Name'
    }
  }

  // UPLOAD IMAGE
  const handleUploadImage = (row: MRT_Row<MetadataItem>) => {
    setSelectedRow(row)
    setImageUploadDialogOpen(true)
  }

  const handleImageUpload = async (file: File) => {
    if (!selectedRow) return

    await updateMetadataImage(metadataType, selectedRow.original.id, file)
    setImageUploadDialogOpen(false)
    setSelectedRow(null)
    await loadItems()
  }

  const getCurrentImageUrl = () => {
    if (!selectedRow) return null
    // Both IBrand and INote have an optional image property
    const item = selectedRow.original as IBrand | INote
    if ('image' in item && item.image) {
      return pb.files.getURL({ id: item.id, collectionName: metadataType }, item.image)
    }
    return null
  }

  return (
    <div className={'flex flex-col h-full'}>
      {/* Fixed Header */}
      <div
        className={
          'flex items-center justify-between px-8 pb-6 pt-8 border-b border-searchBorder'
        }
      >
        <h1 className={'text-2xl font-bold'}>Manage Metadata</h1>

        {/* Dropdown Selector */}
        <select
          value={metadataType}
          onChange={(e) => setMetadataType(e.target.value as MetadataType)}
          className="px-4 py-2 border border-gray-300 rounded-lg bg-white hover:bg-gray-50 transition-colors cursor-pointer focus:outline-none focus:ring-2 focus:ring-black"
        >
          <option value="brands">Brands</option>
          <option value="notes">Notes</option>
          <option value="perfumers">Perfumers</option>
          <option value="countries">Countries</option>
        </select>
      </div>

      {/* Material React Table */}
      <div className={'flex-1 md:overflow-y-auto scrollbar-hide-track'}>
        <MetadataTable
          data={items}
          isLoading={isLoading}
          pagination={pagination}
          rowCount={rowCount}
          onPaginationChange={setPagination}
          onCreate={handleCreate}
          onUpdate={handleUpdate}
          onDelete={handleDelete}
          onUploadImage={
            metadataType === 'notes' || metadataType === 'brands'
              ? handleUploadImage
              : undefined
          }
          entityName={getEntityName()}
          columnHeader={getColumnHeader()}
          collectionName={metadataType}
          searchValue={searchValue}
          onSearchChange={setSearchValue}
          sorting={sorting}
          onSortingChange={setSorting}
        />
      </div>

      {/* Delete Confirmation Dialog */}
      <ConfirmationDialog
        isOpen={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={confirmDelete}
        title={`Delete ${getEntityName()}`}
        content={`Are you sure you want to delete "${selectedRow?.original.name}"? This action cannot be undone.`}
        confirmLabel="Delete"
        cancelLabel="Cancel"
      />

      {/* Image Upload Dialog */}
      <ImageUploadDialog
        isOpen={imageUploadDialogOpen}
        onClose={() => setImageUploadDialogOpen(false)}
        onUpload={handleImageUpload}
        title={`Upload Image for ${selectedRow?.original.name}`}
        currentImageUrl={getCurrentImageUrl()}
      />
    </div>
  )
}
